/**
 * Toutes les constantes réglables du MVP, au même endroit.
 * Source : docs/MVP_DESIGN.md. Modifier ici, jamais dans le code de logique.
 */

// ────────────────────────────────── Courbe de PV par niveau d'expédition
//
// Arbitrages **A12 / A13** (DECISIONS §E) : la croissance des PV d'un niveau
// d'expédition au suivant est une **courbe lisse**, générée par des constantes,
// et non une table saisie palier par palier.
//
// v0.54.0 (**A108**, carnet du GD du 23/10/2026, DECISIONS §D.81) : le rapport
// d'un niveau au précédent est **constant** en campagne, puis un second rapport
// constant prend le relais en mode infini :
//
//     PV(d, l) = base × hpDayGrowth^(d − 1) × Mult_Camp^(l − 1)                  (l ≤ 10)
//     PV(d, l) = base × hpDayGrowth^(d − 1) × Mult_Camp^9 × Mult_infini^(l − 10)  (l > 10)
//
// Mult_Camp = 7,314 (`HP_LEVEL_GROWTH_CAMPAIGN`), Mult_infini = 6,669
// (`hpLevelGrowth`, inchangé). Jusqu'en v0.53.x (A14, §D.19) le rapport partait
// de ×7,80 et décroissait de 2 % par niveau (×6,79 au niveau 10, cumul ×57,0 M) ;
// à 7,314 le cumul du niveau 10 passe à ×59,9 M et celui du niveau 7 de ×173 136
// à ×153 084, toujours **au-dessus** du plafond de puissance de l'arbre complet
// (≈ ×12 300 le mur du niveau 1) : la condition d'A14 — le niveau 7 atteint et
// jamais franchi — tient. Les niveaux 2 à 6 sont 6 à 14 % plus doux qu'avant.

/** Mult_Camp : rapport de PV entre deux niveaux consécutifs, du niveau 1 au niveau 10 (A108). */
const HP_LEVEL_GROWTH_CAMPAIGN = 7.314;
/** Nombre de niveaux couverts par la courbe (les paliers du GD s'arrêtent à 10). */
const HP_LEVEL_COUNT = 10;

/** Multiplicateurs **cumulés** par niveau (index 0 = niveau 1, toujours ×1) : `Mult_Camp^(L − 1)`. */
function buildHpLevelCurve(): readonly number[] {
  const out: number[] = [1];
  for (let level = 2; level <= HP_LEVEL_COUNT; level++) {
    // Arrondi à 3 décimales de la puissance exacte : la courbe reste lisible dans
    // les exports de debug sans changer un seul PV (les PV sont arrondis à l'entier).
    out.push(Math.round(Math.pow(HP_LEVEL_GROWTH_CAMPAIGN, level - 1) * 1000) / 1000);
  }
  return out;
}

/** Nombres premiers de 2 à `max` inclus (crible simple, petits entiers). */
function primesUpTo(max: number): readonly number[] {
  const out: number[] = [];
  for (let n = 2; n <= max; n++) {
    let prime = true;
    for (let d = 2; d * d <= n; d++) {
      if (n % d === 0) {
        prime = false;
        break;
      }
    }
    if (prime) out.push(n);
  }
  return out;
}

/**
 * Version d'équilibrage (A87) : c'est la **saison** des classements. À faire
 * monter par l'orchestrateur à chaque changement d'équilibrage ; chaque
 * enregistrement de télémétrie la porte, le Worker classe par saison.
 */
export const BALANCE_VERSION = '0.58';

export const BALANCE = {
  /** Chronos (secondes) — MVP_DESIGN §2 */
  expeditionDuration: 15,
  /**
   * L'Invasion dure 2 × la durée **de base** d'une Expédition (30 s) ; le bonus
   * de *Journée longue* s'y ajoute une fois, sans être doublé (COMMENTS
   * v0.50.0 §1.1, A101 ; 1,5 × la durée bonus comprise avant la v0.51.0).
   */
  invasionDurationFactor: 2,

  /** Lancer */
  throwCooldown: 1.0,
  throwCooldownFloor: 0.5,
  baseCritChance: 0.05,
  baseCritMultiplier: 1.5,

  /** Dés basiques */
  baseDieSides: 4,
  upgradedDieSides: 6,

  /** Structure de la run — MVP_DESIGN §1 */
  daysPerLevel: 5,
  /** Le jour « virtuel » de l'Invasion pour la formule de vague. */
  invasionDay: 6,
  /** W = (L − 1) × wavesPerLevel + D */
  wavesPerLevel: 6,
  invasionEnemyCount: 5,
  /** Animation d'apparition du monstre suivant (secondes). */
  spawnDelay: 0.25,
  /**
   * Fenêtre de grâce pendant laquelle un lancer déjà tiré (dés « en vol »)
   * peut encore être appliqué après la fin du combat — MVP_DESIGN §1 / DECISIONS D.
   */
  inFlightGrace: 1.5,
  /**
   * Durée maximale d'un combat, en multiple de sa durée de base.
   * Sans ce plafond, *Temporel* + *42* rendent l'Expédition infinie
   * dès que le joueur tue plus d'un ennemi par seconde (DECISIONS D3 / Q12).
   */
  maxCombatFactor: 2,
  /**
   * Plafond d'ennemis d'une **Expédition** (A88, v0.42.0) : le 50e ennemi tué
   * termine l'Expédition, réussie — or acquis, sans attendre le chrono, le
   * temps restant n'est converti en rien. L'Invasion n'est jamais concernée
   * (4 ennemis + boss). `Game.expeditionEnemyCap()`.
   */
  expeditionEnemyCap: 50,

  /** Ennemis — MVP_DESIGN §5 */
  /**
   * Croissance des PV **d'un jour à l'autre à l'intérieur d'un niveau**
   * (D = 1…5, Invasion = 6) — `PV = base × hpDayGrowth^(D−1) × hpLevelCurve[L−1]`.
   *
   * v0.2.3 (COMMENTS §2.2) : 1,18 → 1,12. À 1,18 les PV croissaient plus vite
   * que la puissance du joueur au niveau 1 et le boss n'était pas battable.
   * v0.3.2 (COMMENTS v0.3.0 §3.2) : la valeur **ne bouge pas**, elle ne
   * gouverne plus que le jour — « la croissance du premier est petite devant
   * celle du deuxième ». L'ancien nom `hpGrowth` reste accepté comme clé de
   * surcharge de debug (`BalanceOverrides.hpGrowth`).
   *
   * v0.7.0 (§D.19) : **1,12 → 1,14**, dernier levier utilisé et le plus petit.
   * L'Invasion est le jour 6 : la retoucher revient à multiplier **tous** les
   * murs par (1,14/1,12)^5 ≈ 1,09 sans toucher aux Expéditions, ce qui recale
   * le palier du niveau 2 sans déplacer le mur du niveau 7. Les deux sondes de
   * garde-fou du niveau 1 sont vérifiées à cette valeur (0 % / 100 %) ; elles
   * cassent à partir de 1,25.
   */
  hpDayGrowth: 1.18,
  /**
   * Croissance des PV **au-delà de la courbe** (niveaux > 10), et croissance
   * géométrique de repli quand une surcharge de debug fixe explicitement cette
   * clé — la courbe est alors neutralisée (`src/core/balance.ts`).
   *
   * v0.3.2 → v0.5.0 : **6**, calé sur l'ancienne cible « mur du niveau 3 »
   * (COMMENTS v0.3.0, réponse 18), aujourd'hui caduque (§E / A1).
   * v0.5.1 : **1,12**, c'est-à-dire le rapport qu'aurait le niveau 11 dans la
   * courbe ci-dessous — la prolongation est ainsi continue.
   * v0.7.0 (§D.19) : **6,669**, valeur *dérivée* et non choisie — c'est le
   * rapport qu'aurait le niveau 11, `1 + 6,8 × 0,98^9`.
   * v0.54.0 (A108, §D.81) : **6,669 conservé**, désormais choisi : c'est le
   * *Mult_infini* du carnet du GD, indépendant de *Mult_Camp* (7,314).
   */
  hpLevelGrowth: 6.669,
  /** Mult_Camp : rapport de PV entre deux niveaux consécutifs en campagne (A108) — voir `HP_LEVEL_*` en tête de fichier. */
  hpLevelGrowthCampaign: HP_LEVEL_GROWTH_CAMPAIGN,
  /**
   * **Courbe de PV par niveau**, cumulée : `hpLevelCurve[L−1]` est le
   * multiplicateur de PV du niveau L par rapport au niveau 1, soit
   * `Mult_Camp^(L − 1)`. Générée par `buildHpLevelCurve()` — jamais saisie
   * point par point (arbitrage **A12**).
   *
   * Table obtenue en v0.54.0 (A108, §D.81) :
   *
   * | L | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 |
   * |---|---|---|---|---|---|---|---|---|---|---|
   * | rapport | — | ×7,314 à chaque niveau |
   * | cumul | ×1 | ×7,31 | ×53,5 | ×391 | ×2 862 | ×20 930 | ×153 084 | ×1,12 M | ×8,19 M | ×59,9 M |
   *
   * Le mur du niveau 7 (×153 084) reste **au-dessus** du plafond de puissance
   * de l'arbre complet (≈ ×12 300), ce qui est la condition d'A14 : le joueur
   * atteint le niveau 7 et ne le franchit pas.
   */
  hpLevelCurve: buildHpLevelCurve(),
  /**
   * Croissance de l'or **par vague W** (W = (L−1) × 6 + D).
   *
   * Historique : 1,15 à l'origine, portée à 1,18 en passe QA (§D.5) puis
   * conservée en v0.3.2 alors que les PV passaient à deux multiplicateurs
   * (réponse GD 18 : l'écart or/PV est volontaire).
   *
   * v0.5.1 (§D.17) : **1,18 → 1,04**. À 1,18 le revenu est multiplié par 2,7 à
   * chaque niveau : l'arbre entier (≈ 27 000 or) est payé dès la 2e ou 3e run,
   * le joueur atteint son plafond de puissance avant le niveau 5 et traverse
   * ensuite tous les paliers restants dans la même run — le parcours entier
   * tient en 45 min au lieu des 3 h visées. À 1,04, l'or reste rare assez
   * longtemps pour que chaque palier demande une run de plus, ce qui est
   * exactement la forme demandée par COMMENTS v0.4.1 §1.1. La contrepartie est
   * compensée par l'or de base des ennemis, relevé de 20 % (`data/enemies.ts`).
   *
   * v0.26.0 (COMMENTS v0.25.0 §1.1, A72-18) : **1,04 → 1,1**, à la demande du
   * GD, sans mise à jour de l'espérance ni de la simulation.
   */
  goldGrowth: 1.1,
  /**
   * Croissance de l'or d'un **niveau d'Expédition** à l'autre — COMMENTS
   * v0.6.0 §1.4 / arbitrage **A24** : `or × goldLevelGrowth^(L−1)`, en plus de
   * la croissance par vague `goldGrowth`.
   *
   * v0.8.0 (COMMENTS v0.7.0 §2.1) : le GD ramène le levier de **4,4 à 1,3** —
   * « l'or était gagné beaucoup trop rapidement et l'arbre était débloqué
   * complètement à la fin du niveau 3 d'expéditions ». Les coûts du Gachapon
   * suivent (15 / 45 / 135, §2.2) ; aucune autre constante n'est retouchée, la
   * passe d'équilibrage n'ayant pas été demandée (§F).
   *
   * Historique — v0.7.0 (§D.19) : **1,0 → 7,5**, le levier principal de la passe. L'arbre
   * v0.7.0 coûte 321 636 or en entier ; pour que le joueur moyen l'ait à peu
   * près rempli au niveau 6 — condition pour que le mur du niveau 7 soit un mur
   * de **contenu** et non de portefeuille — son revenu doit croître aussi vite
   * que les PV. Avec `goldGrowth` 1,04, un niveau rapporte
   * ×(1,04^6 × 7,5) ≈ ×9,5 : un peu plus que les ×7,8 → ×7,1 de la courbe de
   * PV, exactement l'avance qu'il faut pour payer des nœuds dont le coût croît
   * en 1,6^niveau. `goldGrowth` reste à 1,04 : c'est la croissance **par
   * vague**, et la relever accélère surtout les niveaux 1 à 3 (mesuré : à 1,20,
   * le niveau 3 tombe 12 min trop tôt et le niveau 6 à 57 min au lieu de 84).
   *
   * v0.26.0 (COMMENTS v0.25.0 §1.1, A72-18) : **1,3 → 1,4**, sans mise à jour
   * de l'espérance ni de la simulation.
   *
   * v0.49.0 (COMMENTS v0.48.1 §1.1, A98-1) : **1,4 → 1,35**, chiffre du GD,
   * sans simulation.
   */
  goldLevelGrowth: 1.35,
  /**
   * *Exécutant* (A122-1) : l'affixe agit sur les ennemis dont les PV sont
   * **strictement supérieurs** à cette part de leurs PV max (80 %). Remplace
   * l'ancien seuil « sous 20 % » (`executeThreshold`).
   */
  executionerHpAbove: 0.8,
  /**
   * *Silicium 5* — *Électrification* (A122-6) : chaque lancer tiré avec le
   * bonus ajoute un cumul (jusqu'à `siliconElectrifyMax`), chaque cumul
   * donne `siliconElectrifyStep` (+1 %) de dégâts au lancer. Remis à zéro à
   * chaque combat. Remplace le dé fantôme.
   */
  siliconElectrifyStep: 0.01,
  siliconElectrifyMax: 50,
  /**
   * *ArchiDicer* (A122-7) : +33 % de dégâts du lancer par sort déclenché
   * (tous les sorts du lancer, copies comprises), bonus plafonné à +100 %
   * (`archidicerCap`). Remplace la garantie d'un sort de dé (A72-16).
   */
  archidicerPerSpell: 0.33,
  archidicerCap: 1,
  /**
   * *Rat-Ailé* (v0.58.0, A123-4) : avec un Dé du Rat-Ailé équipé et **aucun**
   * bonus de Matériau actif, les affixes **à plage** des dés équipés valent
   * ×1,2 (multiplicatif avec Enrichi), plafond du poison compris (30 % → 36 %).
   */
  wingedRatAffixFactor: 1.2,
  /**
   * *Croupier* (v0.58.0, A123-3) : à chaque lancer (Larbin compris), une
   * chance sur deux (`croupierChance`) de ×4 (`croupierHigh`), sinon ×0,01
   * (`croupierLow`), dans le produit des dégâts propres.
   */
  croupierLow: 0.01,
  croupierHigh: 4,
  croupierChance: 0.5,

  /**
   * Effets d'ennemis — COMMENTS v0.7.0 §1 / DECISIONS §F. Seules les constantes
   * **partagées** vivent ici : les chances et pourcentages propres à un ennemi
   * sont dans `data/enemies.ts`, à côté de sa ligne de table.
   *
   * La part de PV rendue à la résurrection, elle, est devenue un réglage **par
   * ennemi** (Slime 15 %, Zombie 20 %, Goule 25 %, Monstruosité 33 %) :
   * l'ancienne constante unique `zombieRevivePercent` (30 %) a disparu.
   */
  /** Durée d'un assommement (Orc Guerrier, Gros Orc, Sirène) — A29. */
  stunDuration: 1.5,
  /** Secondes retirées au chrono par le Gobelin Ingénieur (1 chance sur 3). */
  engineerSeconds: 3,
  /** Multiplicateur de PV du prochain ennemi après un Ingénieur (1 chance sur 3). */
  engineerHpMult: 1.5,
  /** Coups maximum pour tuer le Serpent, sinon le temps restant est divisé par 2 (A28 ; v0.52.0, A102). */
  serpentMaxHits: 3,
  /** Accélération du chrono à la mort d'un Poisson Lune (cumulable). */
  moonfishTimeFactor: 1.1,
  /** Ralentissement des lancers par l'Araignée : temps de retour ÷ (1 − 0,15). */
  spiderSlowPercent: 0.15,

  // ── v0.49.0 — effets des boss (COMMENTS v0.48.1 §1.8, A98), **en plus** de
  // ce qu'ils avaient. Une constante par valeur chiffrée ; le Roi squelette
  // reçoit le bouclier du Squelette à bouclier, sans chiffre.
  /** Saigneur Vampire : 15 % de chance par lancer de diviser les dés Basiques… */
  vampireLordHalveBasicChance: 0.15,
  /** … par 2. */
  vampireLordHalveBasicDivisor: 2,
  /** Veuve Noire : ralentit de 15 % la vitesse de lancer (temps de retour ÷ 0,85). */
  blackWidowSlowPercent: 0.15,
  /** Chef des Orcs (A98-5) : 10 % de chance d'assommer à chaque lancer appliqué… */
  orcChiefStunChance: 0.1,
  /** … pendant 1,5 s. */
  orcChiefStunSeconds: 1.5,
  /** Reine des Sirènes : un dé Maître annulé au hasard par lancer. */
  mermaidQueenVoidDice: 1,
  /** Super-Méca Gobelin : dégâts du lancer ÷ 1,05 par coup reçu… */
  mechaGoblinArmorPerHit: 1.05,
  /** … cumulable 5 fois. */
  mechaGoblinArmorMaxStacks: 5,
  /** Gros Ver des Sables : toutes les 3 s… */
  sandWormTimeTickEvery: 3,
  /** … le chrono perd 1 s. */
  sandWormTimeTickSeconds: 1,
  /** Ancien Roi Maudit : deux dés Maîtres annulés au hasard par lancer. */
  cursedKingVoidDice: 2,
  /** Tricorne : un dé Maître annulé au hasard par lancer… */
  tricornVoidDice: 1,
  /** … dégâts ÷ 1,05 par coup reçu… */
  tricornArmorPerHit: 1.05,
  /** … cumulable 5 fois… */
  tricornArmorMaxStacks: 5,
  /** … et 25 % de chance par lancer de diviser les dés Basiques… */
  tricornHalveBasicChance: 0.25,
  /** … par 2. */
  tricornHalveBasicDivisor: 2,

  // ── v0.50.0 — monstres de COMMENTS v0.49.1 §1.1 (A100).
  /** Sirène : chance d'assommer par seconde, **20 % → 15 %** (COMMENTS v0.49.1 §1.1). */
  mermaidStunChancePerSecond: 0.15,
  /**
   * Mage Maudit (A100-1) : la **part de bonus** de chaque sort est divisée par
   * 2 — `m′ = 1 + (m − 1) ÷ 2` (×1,25 → ×1,125 ; ×0,75 → ×0,875).
   */
  cursedMageSpellDivisor: 2,
  /** Licorne sadique : deux dés Maîtres annulés au hasard par lancer (bloquait les emplacements 1 et 2). */
  sadisticUnicornVoidDice: 2,

  /** Poison — MVP_DESIGN §3.3 */
  poisonDuration: 4,
  /** Plafond de la somme des affixes *Empoisonné* d'un même lancer (30 %). */
  poisonCap: 0.3,

  /**
   * Secondes accordées par un déclenchement de l'affixe *Temporel*.
   * A88 (v0.42.0) : **1 → 0,5**.
   */
  temporalSeconds: 0.5,

  /**
   * Sorts — COMMENTS v0.4.1 §1.4 / DECISIONS §E.
   * Deux familles : les sorts d'**emplacement** (débloqués par le nombre
   * d'emplacements de dés Maîtres) et les sorts de **dé** (portés par le dé).
   */
  /** *Paire* : deux faces identiques (2 emplacements). */
  spellPairMultiplier: 1.25,
  /** *Yeux du serpent* : toutes les faces au minimum (2 emplacements) — malus. */
  spellSnakeEyesMultiplier: 0.75,
  /** *Brelan* : trois faces identiques (3 emplacements). */
  spellTripleMultiplier: 2,
  /** *Double paire* : deux paires de faces différentes (4 emplacements). */
  spellDoublePairMultiplier: 2.5,
  /** *Carré* : quatre faces identiques (4 emplacements). */
  spellFourMultiplier: 4,
  /**
   * *666* — l'**Enfer** (COMMENTS v0.6.0 §1.2.2) : trois 6 sur les dés Maîtres
   * embrasent le sol jusqu'à la fin du combat en cours ; tous les lancers sont
   * alors multipliés par 1,5. Non cumulable.
   */
  spellInfernoMultiplier: 1.5,
  /**
   * *Premiers* (A17) : un nombre **premier** de dés Maîtres affichant une face
   * première multiplie les **trois lancers suivants** par 2, 3 puis 5.
   */
  spellPrimesSequence: [2, 3, 5] as readonly number[],
  /**
   * Plus grande face première reconnue par *Premiers*. v0.43.0 (A89-2) : le
   * d100 se lit **brut** et *Premiers* compte jusqu'à **97** — une face est
   * première si c'est un nombre premier ≤ 97 (`core/combos.ts : isPrimeFace`).
   */
  spellPrimeMaxFace: 97,
  /**
   * Faces considérées comme premières par *Premiers* : tous les nombres
   * premiers jusqu'à `spellPrimeMaxFace`, **calculés** (plus de liste « 2 à
   * 19 » saisie à la main, v0.26.0) — 25 faces, de 2 à 97.
   */
  spellPrimeFaces: primesUpTo(97),
  /** *Maxroll* : toutes les faces au maximum. */
  spellMaxrollMultiplier: 1.75,
  /** *Maxroll* : l'effet est doublé par emplacement au-delà de 2. */
  spellMaxrollSlotFactor: 2,
  /** *Pi* : faces 3, 1 et 4 présentes. */
  spellPiMultiplier: 3.14,
  /** *Parité* : toutes les faces paires **ou** toutes impaires. */
  spellParityMultiplier: 1.5,
  /** *Nombre d'or* : faces 1, 6, 1 et 8 présentes — × l'or de l'ennemi tué. */
  spellGoldenRatioGoldMult: 1.61,
  /** Probabilité qu'un sort tiré soit **rare** (raretés Rare et Épique). */
  dieSpellRareChance: 0.3,

  // ── v0.49.0 — sorts des d12 et d20 (COMMENTS v0.48.1 §1.3.2, A98-4, A98-8).
  /** *Vingt-dicte*, *Null*, *Tempête* : sorts des dés d'au moins 12 faces. */
  spellD12MinSides: 12,
  /** *Rouleau Compresseur* : sort des d20. */
  spellSteamrollerMinSides: 20,
  /** *Vingt-dicte* : au moins 4 faces Maîtres strictement supérieures à 6… */
  spellTwentyDictCount: 4,
  spellTwentyDictFace: 6,
  /** … 3 avec le *Grimoire des nombres* niveau 3 (A98-8). */
  spellTwentyDictWideCount: 3,
  /** *Null* : paires de faces Maîtres tolérées avec le Grimoire niveau 3 (aucune sinon). */
  spellNullWidePairs: 1,
  /** *Tempête* : 3 faces Maîtres strictement supérieures à 10… */
  spellStormCount: 3,
  spellStormFace: 10,
  /** … 2 avec le Grimoire niveau 3. */
  spellStormWideCount: 2,
  /** *Tempête* : durée (s), rafraîchie par un nouveau déclenchement, non cumulée. */
  spellStormDuration: 5,
  /**
   * *Tempête* : réduction du temps de retour du joueur et du *Larbin* (25 %) ;
   * renforcée par le Grimoire des nombres, `0,25 × (1 + 0,1 × niveau)`.
   */
  spellStormReduction: 0.25,
  /** *Rouleau Compresseur* : tolérance sous la plus haute face précédente au Grimoire niveau 3. */
  spellSteamrollerWideSlack: 1,

  /** Affixes d12 — COMMENTS v0.6.0 §1.3 */
  /** *Mutilant* : durée de la mutilation (s). */
  affixMutilatedDuration: 3,
  /** *Mutilant* : dégâts subis par un ennemi mutilé. */
  affixMutilatedFactor: 1.3,

  /**
   * Arbre de talents — MVP_DESIGN §6.
   *
   * v0.9.0 (COMMENTS v0.8.0 §2.1) : **1,6 → 1,55**. « L'objectif est de
   * réduire les coûts surtout pour les derniers niveaux du talent plutôt que
   * d'abaisser le coût initial » — `nodeCost` étant `base × growth^niveau`, la
   * baisse du facteur pèse peu au niveau 1 et de plus en plus aux niveaux
   * élevés, exactement la forme demandée.
   */
  nodeCostGrowth: 1.55,

  /** Effets par niveau de nœud */
  /**
   * *Main lestée* — COMMENTS v0.4.1 §1.2 : « Multiplie les dégâts du lancer
   * par 1,05 », « Gagne +0,05 supplémentaire » par niveau, donc
   * **`1 + 0,05 × niveau`** (facteur de `M_T`). v0.5.0 : la lecture
   * exponentielle `1,05^niveau` de la v0.4.0 est abandonnée au profit de la
   * lecture linéaire retenue par le GD pour *Puissance des sorts* (A2).
   */
  nodeDamageMultPerLevel: 0.05,
  /** Petit lancer : +1 dégât plat par dé **Maître équipé** et par niveau (D_L). */
  nodeFlatDamagePerLevel: 1,
  nodeCritPerLevel: 0.02,
  /** *Touche critique* : +0,05 au **bonus critique additif** (Σ d_C) par niveau. */
  nodeCritDamagePerLevel: 0.05,
  nodeCooldownPerLevel: 0.05,
  nodeGoldPerLevel: 0.1,
  nodeRarityPerLevel: 0.1,
  /**
   * *Puissance des sorts* — arbitrage **A2** (DECISIONS §E) : « ×1,1 au niveau 1,
   * +0,1 par niveau → ×(1 + 0,1 × niveau) ». Appliqué **une fois** au produit
   * des multiplicateurs de sorts du lancer, à ses secondes et à son
   * multiplicateur d'or.
   */
  nodeSpellPowerPerLevel: 0.1,
  /** *Journée longue* : +1 s d'Expédition par niveau (COMMENTS v0.4.1 §1.2). */
  nodeDayLengthPerLevel: 1,
  /** *Mort dorée* : +1 % de chance de doubler l'or d'un ennemi tué, par niveau. */
  nodeGoldDoublePerLevel: 0.01,
  /**
   * *Préparation* — v0.50.0 (COMMENTS v0.49.1 §1.3, A100-2) : renfort des sorts
   * de dé portés par les dés Maîtres des emplacements 1 à 3, **+10 % par
   * niveau** (`prepBoost = 0,1 × niveau`, ×1,5 au niveau 5) ; la part de bonus
   * d'un sort passe à `m′ = 1 + (m − 1) × (1 + prepBoost)`. Avant : +1 % de
   * compteur par lancer sans sort et par niveau (le compteur a disparu).
   */
  nodePrepPerLevel: 0.1,
  /** *Préparation* : emplacements renforcés, de 1 à `nodePrepSlots` (3). */
  nodePrepSlots: 3,
  /** *Dé à lunette* : +2 % de chance de critique par niveau. */
  nodeScopeCritPerLevel: 0.02,
  /** *Critique Magique* : facteur **multiplicatif** du critique, +0,05 par niveau. */
  nodeMagicCritPerLevel: 0.05,
  /** *Machine Critique* : −5 % de temps de retour par niveau après un critique. */
  nodeCritMachinePerLevel: 0.05,
  /** *Machine Critique* : durée de la réduction (s), relancée à chaque critique. */
  critMachineDuration: 5,
  /** *Larcin* : +10 % d'or par niveau (A86, v0.41.0 ; +50 % avant), **additif** avec *Butin*. */
  nodeLarcinGoldPerLevel: 0.1,
  /** *Orsplosion* : +2 % de chance de doubler l'or par niveau, additif avec *Mort dorée*. */
  nodeOrsplosionPerLevel: 0.02,
  /** *Gagnant-Gagnant* : +1 % par niveau d'obtenir un second dé par dé tiré. */
  nodeTwinChancePerLevel: 0.01,
  /** *Sorcellerie* : ×(1 + 0,1 × niveau), multiplicatif avec *Puissance des sorts* (A21). */
  nodeSorceryPerLevel: 0.1,
  /** *Coup de fouet* : −5 % de temps de retour du *Larbin* par niveau. */
  nodeWhipCooldownPerLevel: 0.05,
  /** *Larbin* : temps de retour ×1,25 (A18). */
  minionCooldownFactor: 1.25,
  /** *Larbin* : dégâts de lancer ×0,75 (A18). */
  minionDamageFactor: 0.75,
  /**
   * Négociateur et *Carte de fidélité* : −5 % de prix au Gachapon par niveau,
   * **additifs** entre eux (A72-10) — `prix × (1 − 0,05 × niveaux)`, jusqu'à
   * −50 % aux deux nœuds pleins. (Lecture linéaire depuis la v0.26.0 : la
   * remise composée `0,95^niveau` ne pouvait pas atteindre les −50 % annoncés.)
   */
  nodeGachaDiscountPerLevel: 0.05,

  // ── v0.26.0 — COMMENTS v0.25.0 §1.2 / arbitrage A72 ───────────────────
  /**
   * *Dice Master* (A72-5) : facteur de rareté de chaque dé Maître équipé ; le
   * multiplicateur du lancer est la **moyenne** des facteurs des Maîtres
   * équipés.
   */
  diceMasterFactors: {
    common: 1,
    uncommon: 1.05,
    rare: 1.1,
    epic: 1.25,
    legendary: 1.5,
    prismatic: 2,
  } as Readonly<Record<'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'prismatic', number>>,
  /** *Brouette* : +500 dés Basiques par niveau (A88, v0.42.0 ; +1 000 avant). */
  nodeBarrowDicePerLevel: 500,
  /** *Icosaèdrique* : 20 % des dés du contenant deviennent des d20, par niveau. */
  nodeIcosaSharePerLevel: 0.2,
  /**
   * *Criticité* (A72-3) : facteur `1 + 0,02 × niveau` par critique consécutif,
   * appliqué en `facteur^(série − 1)` dès le 2e (A72-4) ; plafond de 10 cumuls
   * depuis la v0.49.0 (`streakMaxStacks`).
   * A88 (v0.42.0) : **0,05 → 0,02** par niveau (×1,10 au niveau 5).
   */
  nodeCritStreakPerLevel: 0.02,
  /**
   * *Flux arcanique* (A72-3) : même forme que *Criticité*, sur les lancers à
   * sort. A88 (v0.42.0) : **0,05 → 0,02** par niveau.
   */
  nodeSpellStreakPerLevel: 0.02,
  /**
   * *Criticité* et *Flux arcanique* (COMMENTS v0.48.1 §1.2, A98-1) : la série
   * est **plafonnée à 10 cumuls** — `facteur^min(série − 1, 10)`
   * (`core/combat.ts : streakMultiplier`).
   */
  streakMaxStacks: 10,
  /** *Esclavagisme* (A72-12) : +0,1 au facteur de dégâts du Larbin par niveau. */
  nodeSlaveryPerLevel: 0.1,
  /**
   * *Arrière-garde* (A89-6) : renfort des sorts de dé de dégâts portés par les
   * deux derniers dés Maîtres équipés, par niveau —
   * `m' = 1 + (1 + 0,04 × niveau) × (m − 1)`.
   */
  nodeRearguardPerLevel: 0.04,
  /**
   * *Tractopelle* (A72-17) : au-delà de ce nombre de dés supplémentaires, la
   * somme de leurs faces est tirée par **approximation normale** (moyenne et
   * variance exactes, bornée à [n, n × faces]) au lieu d'un tirage dé par dé —
   * le compteur n'a pas de plafond et un tirage exact de millions de dés
   * figerait le jeu. En deçà, tirage exact.
   */
  backhoeExactLimit: 100_000,
  /** Prix plancher d'un dé au Gachapon. */
  gachaMinCost: 1,


  /** Contenant (Gobelet) — COMMENTS v0.4.1 §1.2, nœuds `ars_goblet` / `ars_hex` */
  /**
   * *Gobelet* : dés Basiques accordés **par niveau** du nœud (2, 4, 6, 8, 10).
   * Le contenant est entièrement dérivé de l'arbre de talents, jamais rempli
   * à la main (`core/container.ts`).
   */
  gobletDicePerLevel: 2,
  /**
   * *Contenant hexagonal* : part de dés d6 dans le contenant **par niveau**
   * (20 %). `nombre de d6 = round(nombre de dés × 0,2 × niveau)`, le reste en
   * d4 ; au niveau 5 le contenant est intégralement en d6.
   */
  nodeHexSharePerLevel: 0.2,
  /** *Bol* : +10 dés Basiques par niveau (COMMENTS v0.6.0 §1.1). */
  nodeBowlDicePerLevel: 10,
  /** *Seau* : +50 dés Basiques par niveau (A88, v0.42.0 ; +100 avant). */
  nodeBucketDicePerLevel: 50,
  /**
   * *Dodécaédrique* : part de d12 dans le contenant **par niveau** (20 %).
   * Les d12 sont servis **avant** les d6 (`core/container.ts`).
   */
  nodeDodecaSharePerLevel: 0.2,
  // ── v0.46.0 — mode infini, branche de l'Abîme (A94) ───────────────────
  /**
   * Ennemis tués par Expédition dans `Exp(N)`, « une Expédition du niveau N en
   * or » (révision du 15/10, §2) : `core/economy.ts : expeditionGoldRef`.
   */
  expeditionKillsRef: 8,
  /** Un niveau de nœud de l'Abîme s'ouvre tous les deux niveaux d'Expédition (A91, A94-3). */
  abyssGateStep: 2,
  /** Prix d'un niveau de nœud de l'Abîme : 3 × Exp(porte de ce niveau) (A94-1). */
  abyssNodeCostExpeditions: 3,
  /** *Sans fond* : +5 % de dégâts par niveau (A94-12). */
  bottomlessDamagePerLevel: 0.05,
  /** *Sans fond* : prix × 2 à chaque niveau (A94-12). */
  bottomlessCostGrowth: 2,
  /**
   * Niveau d'un dé Maître : dégâts de la face × dieLevelFactor^niveau (A94-5) —
   * le *Mult_Niveau* du carnet. v0.46.0 : **1,5**. v0.54.2 (A110, §D.83) : **2**,
   * pour que le dé suive mieux les monstres (×7,314 puis ×6,669 par Expédition) ;
   * une face 10 au niveau 1 vaut alors 1,34 % des PV du jour 1 de la 11e
   * Expédition d'un monstre ordinaire moyen (1 % avec 1,5), `corruptedFlatFactor`
   * étant gardé à 2 400 000.
   */
  dieLevelFactor: 2,
  /** Plafond de niveau d'un dé sans *Marteau Trempé* (A94-5). */
  dieLevelMax: 20,
  /**
   * Niveau d'un dé tiré aux *Corrupted GachaDice* : **toujours 1** (A105,
   * 21/10/2026 : « les dés quand ils sont drop sont toujours niveau 1 ») ; seul
   * le Trempage fait monter le niveau. De 1 à 10 avant la v0.53.2 (A94-5).
   */
  dieLevelGachaMin: 1,
  dieLevelGachaMax: 1,
  /** *Marteau Trempé* : +2 au plafond de niveau des dés, par niveau. */
  hammerDieLevelPerLevel: 2,
  /** *Marteau Trempé* : −5 % sur le prix des recettes de forge, par niveau (−50 % au niveau 10). */
  hammerForgeDiscountPerLevel: 0.05,
  /**
   * Forge (COMMENTS v0.51.0 §1.2, A102) : le prix **en or** d'une recette de
   * l'Artisan (Relance, Trempage, Trucage) croît de **10 %** à chaque usage sur
   * le dé (niveau actuel du dé pour le Trempage) ; ne porte pas sur les dés
   * sacrifiés. Avant la v0.52.0 : rapport Exp(N + 1) / Exp(N) ≈ 2,39 (A101).
   */
  forgeGoldGrowth: 1.1,
  /**
   * **Trempage** (A104, 21/10/2026 — carnet « Calcul des dés ») : le prix en or
   * du Trempage vaut `goldBase × temperGoldGrowth^niveau actuel du dé`, soit
   * 200 000 × 1,55^N. Calé pour que le niveau 20 d'un dé coûte à peu près une
   * Expédition du niveau 20 (M_R = (Exp(20) / C_R)^(1/20) ≈ 1,52 avec
   * Exp(20) ≈ 872 M sans Butin ni Larcin, 1,57 avec l'arbre complet ; le GD a
   * fixé 1,55). Relance et Trucage gardent `forgeGoldGrowth` par usage.
   */
  temperGoldGrowth: 1.55,
  /** Chance d'un dé **Enrichi** aux *Corrupted GachaDice*, avec *Matériau Enrichi*. */
  enrichedChance: 0.1,
  /** Dé Enrichi : valeur de ses affixes × 1,5. */
  enrichedAffixFactor: 1.5,
  /**
   * Prix de base d'un dé aux *Corrupted GachaDice*, en or, le même à tous les
   * niveaux d'Expédition (COMMENTS v0.50.0 §1.2, A101 ; 1 Exp(niveau courant)
   * avant la v0.51.0).
   */
  corruptedBaseCost: 100_000,
  /**
   * × 1,001 par dé déjà acheté à cette machine (A94-13) ; le compteur repart
   * de zéro à la mort du joueur (A101).
   */
  corruptedCostGrowth: 1.001,
  /** *Sublimation* : chance que le dé devienne Enrichi (A94-9). */
  sublimateChance: 0.1,
  /**
   * Dés **Corrupted** (COMMENTS v0.48.1 §1.6, A98-6) : dans `D_L`, la face d'un
   * dé tiré aux *Corrupted GachaDice* (ou issu d'une Fusion) vaut
   * `face × dieLevelFactor^niveau × corruptedFlatFactor` — le *Mult_Corrupted* du carnet.
   * v0.49.0 : **1 000** (« niveau 5 pour dépasser une brouette »).
   * v0.54.1 (A109, §D.82) : **2 400 000**, calé pour qu'un **10** au niveau 1
   * (`10 × 1,5 × Mult_Corrupted`) vaille **1 %** des PV du jour 1 de la 11e
   * Expédition d'un monstre ordinaire moyen (base 9 : 3,59 Md de PV, cible
   * 2 396 661, arrondie). A110 garde cette valeur avec `dieLevelFactor` 2.
   * v0.55.2 (A114) : **5 000**, demande du GD en séance (« passe le multiple de
   * dés corrupted à 5000 »), `dieLevelFactor` reste 2.
   */
  corruptedFlatFactor: 5_000,

  // ── v0.47.0 — mode infini, lot 2 : Matériaux, Poker, Grimoires (A94, A95) ──
  /**
   * Pièces d'un Matériau : nombre de dés Maîtres **équipés** du même Matériau
   * (A95-1). Bonus à 3 pièces avec *Matériau*, à 5 avec *Matériologie*.
   */
  materialSetSmall: 3,
  materialSetLarge: 5,
  /** *Triple paire* (6 dés) : trois paires de faces différentes, ×3,5 (A94-10). */
  spellTriplePairMultiplier: 3.5,
  /** *Double brelan* (6 dés) : deux brelans de faces différentes, ×5 (A94-10). */
  spellDoubleTripleMultiplier: 5,
  /** *Grande suite* (6 faces consécutives) : × plus haute face² (A94-10). */
  spellGrandStraightPower: 2,
  /** *Maxi suite* (7 faces consécutives) : × plus haute face³ (A94-10). */
  spellMaxiStraightPower: 3,
  /**
   * Les deux Grimoires (A94-11) : pour un multiplicateur de sort m > 1,
   * m′ = 1 + (m − 1) × (1 + 0,1 × niveau) ; secondes et or × (1 + 0,1 × niveau).
   */
  grimoirePerLevel: 0.1,
  /** Conditions élargies (Grimoire des nombres) et Suites à trou (Face je gagne) au niveau 3. */
  grimoireWidenLevel: 3,
  /** *Face je gagne* : Carré dès trois dés identiques au niveau 5. */
  grimoireFourLevel: 5,
  /** Boisé 3 : sous 5 s de chrono en Invasion, +10 s une fois par Invasion (A94-16). */
  woodInvasionThreshold: 5,
  woodInvasionSeconds: 10,
  /** Osseux 3 : ennemi brisé ×1,5 ; Osseux 5 : +0,5 par nouveau bris (A94-14). */
  boneBrokenMultiplier: 1.5,
  boneBrokenStep: 0.5,
  /** Ferreux 3 : dés Basiques ×1,25 ; Ferreux 5 : ×2 par face Maître maximale. */
  ironBasicMultiplier: 1.25,
  ironMaxFaceFactor: 2,
  /** Caoutchouc : emplacements qui rebondissent, 1 à 3 pièces, 3 à 5 pièces (A94-16). */
  rubberSlotsSmall: 1,
  rubberSlotsLarge: 3,

  /** Gachapon — MVP_DESIGN §3.4 */
  gachaLots: [1, 5, 10, 20, 50, 100] as const,
} as const;

export type GachaLot = (typeof BALANCE.gachaLots)[number];
